import {
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileZipOutlined,
  LinkOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  PrinterOutlined,
  QrcodeOutlined,
  SendOutlined,
  TagsOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Grid,
  Input,
  Modal,
  message,
  Popconfirm,
  QRCode,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clearCompanyPick, NewTicketCompanyPicker } from '../components/NewTicketCompanyPicker';
import TicketImportModal from '../components/TicketImportModal';
import TicketOwnerCell from '../components/TicketOwnerCell';
import api, { FRONTEND_URL } from '../config/api';
import { FALLBACK_CURRENCY, fetchEventCurrencies, formatMoney } from '../config/eventCurrency';
import { ATTENDEE_TYPES, attendeeTypeLabelKey } from '../i18n/attendeeType';
import { textLocaleTag } from '../i18n/locale';
import { isRevenueBearing } from '../utils/guestRevenue';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

interface Ticket {
  id: string;
  ticketNumber: string;
  // A hét érték, amit a backend ténylegesen ír a jegyre; az `EXPIRED` nem
  // volt köztük (nem `TicketStatus`-érték), a lejárt állapot a `PAYMENT_EXPIRED`.
  status:
    | 'PENDING'
    | 'PAID'
    | 'USED'
    | 'CANCELLED'
    | 'TICKET_REQUESTED'
    | 'PAYMENT_INSTRUCTIONS_SENT'
    | 'PAYMENT_EXPIRED';
  purchasedAt: string;
  qrCode?: string;
  ticketTypeId: string;
  userId?: string; // Optional for guest tickets
  eventId: string;
  organizationId: string;
  companyId?: string; // Optional company association
  // Direct attendee data (for guest tickets)
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  // #167 — a névtáblára kiírt szervezet, szabad szövegként. NEM az
  // `organizationId` (az a bérlő azonosítója), és nem is a `Company` sor.
  // Opcionális: régebbi szerver nem küldi, és ettől a lista működik.
  organizationName?: string | null;
  checkedIn?: boolean;
  checkedInAt?: string;
  // #165 — a jegy KATEGÓRIÁJA (hét érték), nem a `TicketType` árazott terméke.
  // Opcionális: egy régebbi szerver nem küldi, és ettől a lista működik.
  attendeeType?: string;
  // #165 — a szponzor-hivatkozás. Ez zárja az átsorolást: a szponzori hely
  // (`attendeeType === 'SPONSOR'` + `sponsorId`) a #148 óta a jelentkezés
  // SZÁMLÁZOTT összegét is meghatározza.
  sponsorId?: string | null;
  TicketType?: {
    id: string;
    name: string;
    price: number;
    description?: string;
  };
  Event?: {
    id: string;
    title: string;
    slug: string;
    startDate: string;
    location?: string;
  };
  User?: {
    // Optional for guest tickets
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
  Company?: {
    id: string;
    name: string;
    taxNumber?: string;
  };
  // #116 — a `Company` sor csak akkor jön létre, ha a jelentkezéskor volt
  // adószám. A cégnév viszont a jelentkezésen akkor is ott van.
  Registration?: {
    companyName?: string;
  };
}

/**
 * A szűrősor jegytípus-választójának adata (#166). Szándékosan szűk: a szűrőnek
 * csak az azonosító és a megjelenített név kell, a teljes jegytípus-rekord nem.
 */
interface FilterTicketType {
  id: string;
  name: string;
}

const Tickets: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [allTicketsStats, setAllTicketsStats] = useState<any>(null);
  const [filteredTicketsCount, setFilteredTicketsCount] = useState(0);
  /**
   * A lap eseménylistája — az inaktívakat IS tartalmazza (#167).
   *
   * A mezők a ténylegesen használt háromra vannak szűkítve (a többi állapot
   * még `any[]`, örökség), hogy az `isActive` elgépelése fordítási hiba
   * legyen, ne néma „mindig aktív" viselkedés.
   */
  const [events, setEvents] = useState<
    { id: string; title: string; isActive?: boolean; organizationId: string }[]
  >([]);
  const [ticketTypes, setTicketTypes] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [filterEventId, setFilterEventId] = useState<string | undefined>(undefined);
  const [filterCompanyId, setFilterCompanyId] = useState<string | undefined>(undefined);
  // #165 — kategória-szűrő. SZERVEROLDALI, szándékosan: a lapozó darabszáma
  // így a szűrt halmazé lesz, és a szűkítés nem csak a betöltött oldalra hat.
  // A lap három meglévő szűrője (cég, jegytípus, kiosztottság) csak állapotot
  // állít és sosem megy ki — ez nem lehet a negyedik ilyen.
  const [filterAttendeeType, setFilterAttendeeType] = useState<string | undefined>(undefined);
  const [filterTicketType, setFilterTicketType] = useState<string | undefined>(undefined);
  /**
   * #166 — a SZŰRŐSOR jegytípusai, szándékosan külön a modáliséitól
   * (`ticketTypes`). A kettő két különböző eseményhez tartozhat: a szervező
   * szűrhet az egyik eseményre, miközben a másikra ad ki jegyet. Egy közös
   * állapot azt jelentené, hogy a modális megnyitása némán átírja a szűrő
   * kínálatát — és fordítva.
   */
  const [filterTicketTypes, setFilterTicketTypes] = useState<FilterTicketType[]>([]);
  /**
   * #166 — a kereső KÉSLELTETETT értéke. A beviteli mező továbbra is azonnal
   * frissül (`searchText`), de a kérés csak akkor indul, ha a gépelés 300 ms-ra
   * megállt. Eddig minden leütés saját kérést küldött: egy négybetűs szó
   * négyet, és ezek versenyeztek egymással.
   */
  const [debouncedSearch, setDebouncedSearch] = useState('');
  /**
   * #166 — SORRENDŐR. A késleltetés önmagában nem elég: két kérés akkor is
   * lehet egyszerre úton, ha a szervező vár egy kicsit, majd tovább gépel. A
   * hibakép az, hogy a lista beáll a helyes találatra, majd magától visszaugrik
   * egy korábbi keresés eredményére, és semmi nem mutatja, miért. Minden kérés
   * kap egy sorszámot; csak a LEGUTÓBBI írhat állapotot.
   */
  const requestSeq = useRef(0);
  /**
   * #122 — a CSEMPÉK saját sorrendőre. Szándékosan KÜLÖN számláló, nem a
   * `requestSeq`: a lista és a csempék két független kérés, és egy közös
   * számlálón a később induló némán érvénytelenítené a másikat, tehát az egyik
   * fél állapota sosem íródna ki.
   *
   * Enélkül a táblázat őrizve van, a fölötte lévő négy csempe nem — és mivel a
   * kettő UGYANARRÓL a szűrt halmazról beszél, a késve érkező régi válasz a
   * lehető legrosszabb képet állítja elő: a lista egy jegyet mutat, a csempe
   * négyet. A szervező ebből azt olvassa ki, hogy a saját szűrője hazudik;
   * hibaüzenet nincs, és az oldal újratöltése „megjavítja".
   */
  const statsSeq = useRef(0);
  /**
   * #166 — a Státusz oszlop fejlécszűrője. Eddig antd KLIENSOLDALI `onFilter`-je
   * volt, tehát csak a betöltött ötven soron szűrt: egy 176 jegyes eseményen a
   * szervező „Lemondva"-ra szűrt, három sort kapott, és abból arra
   * következtetett, hogy három lemondás van. A szerver a `status` paramétert
   * MA IS ismeri — a panel egyszerűen sosem küldte el.
   */
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [filterAssignmentStatus, setFilterAssignmentStatus] = useState<string | undefined>(
    undefined
  );
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [magicLinkModalVisible, setMagicLinkModalVisible] = useState(false);
  const [emailPreviewModalVisible, setEmailPreviewModalVisible] = useState(false);
  const [printExportModalVisible, setPrintExportModalVisible] = useState(false);
  const [editingTicket, setEditingTicket] = useState<Ticket | null>(null);
  // #167 — ugyanaz a kifejezés, amiből a `handleEdit` a mezőt kitölti: amit a
  // szervező előre kitöltve lát, azt nem ürítheti ki abban a hitben, hogy törli.
  const editingTicketHasEmail = Boolean(editingTicket?.User?.email || editingTicket?.email);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [selectedMagicLinkTicket, setSelectedMagicLinkTicket] = useState<Ticket | null>(null);
  const [emailPreviewTicket, setEmailPreviewTicket] = useState<Ticket | null>(null);
  const [emailPreviewData, setEmailPreviewData] = useState<any>(null);
  const [magicLink, setMagicLink] = useState<string>('');
  /**
   * Eseményenkénti pénznem, a védett configból (#111 utómunka).
   *
   * Azért ÁLLAPOT és nem a statisztika-számoló helyi változója, mert a jegy
   * részleteinek és a szerkesztő űrlapnak is ugyanez kell: azok eddig `€`-t
   * írtak ki bedrótozva, tehát egy dináros eseményen ellentmondtak a fenti
   * bevétel-csempének. Amelyik eseményről nem tudjuk, az HIÁNYZIK a
   * térképből — nem `'EUR'`-ként szerepel benne.
   */
  const [currencyByEventId, setCurrencyByEventId] = useState<Map<string, string>>(new Map());
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();

  /**
   * #122 — A LAP SZŰRŐI, EGYETLEN HELYEN.
   *
   * A táblázat és a négy összegző csempe KÉT külön kérésből dolgozik ugyanarról
   * a végpontról, és eddig csak az egyik volt szűrve: a lista az esemény
   * jegyeit mutatta, a fölötte lévő „Bevétel" csempe viszont az egész szervezet
   * összegét — a szervező pedig ezt a kiválasztott esemény bevételeként
   * olvasta. Két külön paraméter-lista óhatatlanul szétcsúszik, ezért van
   * egy forrás: amit itt felveszünk, azt MINDKÉT olvasás megkapja.
   */
  const buildFilterParams = (): Record<string, string> => {
    const params: Record<string, string> = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (filterEventId) params.eventId = filterEventId;
    // Csak ha van érték: üres sztringet a szerver ismeretlen kategóriaként
    // 400-zal utasítana el.
    if (filterAttendeeType) params.attendeeType = filterAttendeeType;
    // A cég-legördülő KÉT dolgot kever: az első két eleme MÓD, a többi valódi
    // cégazonosító. Ha a módok is `companyId`-ként mennének ki, a szerver egy
    // „corporate" nevű céget keresne, üres listát adna, és a szervező ebből azt
    // olvasná ki, hogy nincs céges jegy.
    if (filterCompanyId === 'corporate' || filterCompanyId === 'individual') {
      params.companyMode = filterCompanyId;
    } else if (filterCompanyId) {
      params.companyId = filterCompanyId;
    }
    // Kiosztottság: a `Ticket`-en nincs `userId` (mérve 2026-09-16), ezért ez
    // az e-mail cím meglétére szűr — ugyanarra, amire a lista címkéje.
    if (filterAssignmentStatus) params.assignment = filterAssignmentStatus;
    // A jegytípus ESEMÉNYHEZ tartozik, ezért a mező esemény nélkül le van tiltva.
    if (filterTicketType) params.ticketTypeId = filterTicketType;
    if (filterStatus) params.status = filterStatus;
    return params;
  };

  const fetchTickets = async (page = 1, pageSize = 50) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      // Build search params — a szűrők a KÖZÖS építőből (#122).
      const params: any = {
        page,
        limit: pageSize,
        ...buildFilterParams(),
      };

      const response = await api.get('/tickets', { params });

      // #166 — közben újabb kérés indult: ennek a válasza már elavult, és ha
      // kiírnánk, a frissebb listát verné felül.
      if (seq !== requestSeq.current) return;

      // Handle paginated response
      if (response.data && response.data.data) {
        setTickets(response.data.data);
        // Set filtered count from meta.total (filtered results count)
        setFilteredTicketsCount(response.data.meta?.total || response.data.data.length);
      } else if (Array.isArray(response.data)) {
        setTickets(response.data);
        setFilteredTicketsCount(response.data.length);
      } else {
        setTickets([]);
        setFilteredTicketsCount(0);
      }
    } catch (error) {
      if (seq !== requestSeq.current) return;
      message.error(t('tickets.loadFailed'));
      setTickets([]);
    } finally {
      // A töltésjelzőt is csak a legutóbbi kérés kapcsolhatja ki, különben egy
      // elavult válasz beérkezése „kész"-nek mutatná a még futó lekérdezést.
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  const fetchAllTicketsStats = async () => {
    const seq = ++statsSeq.current;
    try {
      // A `/events` lekérése innen KIKERÜLT (#111): a pénznemet addig olvasta
      // belőle, ameddig a válasz még tartalmazta a `pricing`-ot. A #74 óta nem
      // tartalmazza, és ez a függvény semmi másra nem használta a listát.
      // #122 — UGYANAZOK a szűrők, mint a táblázaté. Enélkül a négy csempe a
      // teljes szervezetet összegzi a szűrt lista fölött.
      const ticketsRes = await api.get('/tickets', {
        params: { page: 1, limit: 10000, ...buildFilterParams() },
      });

      const allTickets: Ticket[] = Array.isArray(ticketsRes.data)
        ? ticketsRes.data
        : (ticketsRes.data?.data ?? []);

      // #164 — az „Összes jegy" csempe száma a `meta.total`-ból jön, nem a
      // betöltött tömb hosszából. A fenti kérés 10 000-es korláttal megy: a
      // tömb hossza e fölött csendben 10 000-en állna meg, és a csempe egy
      // olyan számot írna ki, ami nem a szervezet jegyszáma. Az összegek
      // (`paid`, `used`, bevétel) szükségszerűen a betöltött sorokból
      // készülnek — azokhoz a sorok kellenek, nem csak a darabszám.
      const reportedTotal = ticketsRes.data?.meta?.total;
      const statsTotal = typeof reportedTotal === 'number' ? reportedTotal : allTickets.length;

      // A pénznem a VÉDETT `/events/:id/config`-ból jön (#111).
      //
      // A `GET /events` `@Public()`, és a #74 óta a `config`-ja szűkített:
      // `pricing` nincs benne, tehát az `e.config.pricing.currency` MINDEN
      // eseményre `undefined`, és a bevétel-csempék az egész listát EUR-ként
      // címkéznék. Csak megjelenítés — az összegek nem változnak —, de a
      // szervező ezt olvassa.
      //
      // Csak azokat az eseményeket kérdezzük le, amikhez tényleg tartozik jegy.
      // A lekérés HATOS CSOMAGOKBAN megy, nem egyetlen `Promise.all`-lal: a
      // backend `short` korlátja 30 kérés/másodperc kliens-címenként, tehát 31
      // jegyes eseménytől a kérések egy része 429-et kapott volna.
      const eventIdsWithTickets = [...new Set(allTickets.map((t) => t.eventId))].filter(
        (id): id is string => Boolean(id)
      );
      const currencies = await fetchEventCurrencies(eventIdsWithTickets);

      // Közben újabb szűrő lépett életbe: ennek a válasza már elavult. A
      // vizsgálat CSAK az összes `await` UTÁN áll meg — a pénznem-lekérés
      // maga is több körben megy, és bőven hosszabb lehet a listáénál.
      if (seq !== statsSeq.current) return;
      setCurrencyByEventId(currencies);

      const revenueByCurrency: Record<string, number> = {};
      const paidRevenueByCurrency: Record<string, number> = {};
      // Amelyik esemény pénznemét NEM tudtuk lekérni, az ide gyűlik, külön.
      //
      // Korábban az ilyen jegyek az euró-vödörbe kerültek, mert a hibaág
      // `'EUR'`-t adott vissza. Egy 6000 dináros esemény bevétele így beleadódott
      // az euró-összegbe, és a szervező egy olyan számot olvasott, ami nem
      // létezik. Egy hiányzó lekérés kevesebb tudás, nem más tudás — tehát
      // külön sor, saját megjelöléssel.
      let unknownRevenue = 0;
      let unknownPaidRevenue = 0;
      for (const t of allTickets) {
        const ccy = currencies.get(t.eventId) ?? null;
        // #167 — a vendég nem fizet, tehát NULLÁVAL járul hozzá a bevételhez.
        //
        // Nulla, és nem `continue`: a jegynek akkor is meg kell nyitnia a
        // pénznem-vödrét, különben egy csupa vendégből álló nézetben — például
        // a Vendég kategóriaszűrő alatt — a térkép üres marad, és a csempe a
        // default EUR-ra esik vissza egy dináros eseményen. Az a #111-ben
        // javított hibaosztály: rossz pénznem-címke a szervező szeme előtt.
        //
        // CSAK a bevételből esik ki: a darabszámok (`total`, `paid`, `used`)
        // külön szűrőkkel készülnek, és a vendéget továbbra is számolják — a
        // vendég valódi résztvevő, aki nem fizetett.
        const price = isRevenueBearing(t) ? t.TicketType?.price || 0 : 0;
        // "Ebből fizetve" — csak a már ténylegesen befizetett jegyek.
        // A `USED` itt szándékosan marad (#20): a jelenlétet mostantól a
        // `checkedIn` hordozza, de a RÉGI sorokon a `USED` egy kifizetett
        // jegyet jelöl, és azokat a bevételből kivenni visszamenőleg rontaná
        // el a kimutatást.
        const isPaid = t.status === 'PAID' || t.status === 'USED';
        if (ccy === null) {
          unknownRevenue += price;
          if (isPaid) unknownPaidRevenue += price;
          continue;
        }
        revenueByCurrency[ccy] = (revenueByCurrency[ccy] || 0) + price;
        if (isPaid) {
          paidRevenueByCurrency[ccy] = (paidRevenueByCurrency[ccy] || 0) + price;
        }
      }

      setAllTicketsStats({
        total: statsTotal,
        paid: allTickets.filter((t: Ticket) => t.status === 'PAID').length,
        // #20: a belépettek száma a `checkedIn`-ből jön. Korábban `USED`-re
        // szűrt, amit az ajtó sosem írt — a kártya `0 / 141`-et mutatott,
        // miközben a résztvevők bent voltak.
        used: allTickets.filter((t: Ticket) => t.checkedIn).length,
        pending: allTickets.filter((t: Ticket) => t.status === 'PENDING').length,
        revenueByCurrency,
        paidRevenueByCurrency,
        unknownRevenue,
        unknownPaidRevenue,
      });
    } catch (error) {
      if (seq !== statsSeq.current) return;
      console.error('Failed to fetch all tickets stats:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      const all = response.data.data || [];
      /**
       * #167 — MINDEN esemény bekerül, az inaktívak is.
       *
       * Korábban a lista `isActive !== false`-ra volt szűrve, és ebből az egy
       * listából dolgozott a szűrősáv ÉS a felviteli űrlap is. Emiatt egy
       * inaktív eseményre kiadott jegy a panelen SEHOL nem volt látható —
       * dróton mérve (2026-09-16, 20 jegy): szűretlen lista 0, névkeresés 0,
       * szervezet-keresés 0, kategóriaszűrő 0, és csak az `eventId`
       * paraméterrel jött vissza mind a 20. Azt a paramétert viszont a panel
       * kizárólag ebből a legördülőből tudja beállítani, tehát a szervező nem
       * tudott hozzáférni a saját jegyeihez.
       *
       * A kézi felvitel a #167 óta nem kíván aktív eseményt, a kézikönyv ezt
       * ígéri is — így a felvitel és a MEGTALÁLÁS ugyanazt a listát kapja. Az
       * inaktív eseményt mindkét legördülő megjelöli, lásd `eventOptionLabel`.
       */
      setEvents(all);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  /**
   * Egy esemény felirata a legördülőkben (#167).
   *
   * MINDKÉT legördülő ezt használja — a szűrősávé és a felviteli űrlapé —,
   * mert ugyanabból a listából dolgoznak, és a jelölés nem díszítés: enélkül a
   * szervező nem látja, hogy lezárt eseményre ad ki jegyet, illetve lezárt
   * esemény jegyeit nézi.
   */
  const eventOptionLabel = (event: { title: string; isActive?: boolean }) =>
    event.isActive === false ? `${event.title} ${t('tickets.inactiveEventSuffix')}` : event.title;

  const fetchEventTicketTypes = async (eventId: string) => {
    try {
      console.log('Fetching ticket types for event:', eventId);
      const response = await api.get(`/tickets/types/event/${eventId}`);
      console.log('Ticket types received:', response.data);
      setTicketTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch ticket types:', error);
      setTicketTypes([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      setCompanies([]);
    }
  };

  // Fetch users based on selected company
  const fetchCompanyUsers = async (companyId: string) => {
    try {
      const response = await api.get(`/companies/${companyId}`);
      const companyUsers = response.data?.users || [];
      setUsers(companyUsers);
    } catch (error) {
      console.error('Failed to fetch company users:', error);
      setUsers([]);
    }
  };

  // #122 — a lista és a csempék első betöltése NEM innen indul, hanem a lenti
  // szűrő-effektből, ami a mountkor ugyanúgy lefut. Amíg mindkettő kérte, minden
  // betöltés két listakérést és két 10 000 soros csempe-kérést fizetett, és a
  // sorrendőr az elsőt csak eldobta. Itt csak az marad, ami nem a szűrőktől függ.
  useEffect(() => {
    fetchEvents();
    fetchUsers();
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #166 — a gépelés csillapítása. A takarítás miatt minden újabb leütés
  // eldobja az előző időzítőt, tehát csak a gépelés VÉGE indít kérést.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // #166 — a szűrősor jegytípusai az ITT kiválasztott eseményhez.
  //
  // A korábbi jegytípus-választás ürítése NEM itt történik, hanem az esemény
  // váltásával EGY lépésben (`handleFilterEventChange`, és a „Szűrők törlése"
  // gomb). Innen, egy effektből ürítve egy KÜLÖN renderben állt át, tehát a
  // szűrő-effekt kétszer futott: először az új eseménnyel és a régi esemény
  // jegytípusával — egy egyetlen sorra sem illő kombinációval —, aztán a
  // helyessel (#122).
  useEffect(() => {
    if (!filterEventId) {
      setFilterTicketTypes([]);
      return;
    }
    let cancelled = false;
    api
      .get(`/tickets/types/event/${filterEventId}`)
      .then((response) => {
        if (!cancelled) setFilterTicketTypes(response.data || []);
      })
      .catch(() => {
        if (!cancelled) setFilterTicketTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [filterEventId]);

  // Re-fetch tickets when search or filters change
  useEffect(() => {
    fetchTickets(1, 50);
    // #122 — a csempék a szűrővel együtt mozognak. Ha csak a táblázat töltődne
    // újra, a lap két fele két különböző halmazról beszélne.
    fetchAllTicketsStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // #166 — mind az öt szűrő benne van. A jegytípus és a kiosztás eddig ki sem
    // volt kötve, tehát a választás után NEM indult újratöltés: a legördülő
    // átállt, a lista nem.
  }, [
    debouncedSearch,
    filterEventId,
    filterCompanyId,
    filterAttendeeType,
    filterTicketType,
    filterAssignmentStatus,
    filterStatus,
  ]);

  // #166/#122 — az esemény és a jegytípus EGYÜTT vált. Egy másik esemény
  // jegytípusának azonosítója az új eseményen egyetlen sorra sem illene, és a
  // lista némán üresen maradna; az egy kezelőben állított két érték pedig egy
  // renderbe, tehát egyetlen újratöltésbe esik.
  const handleFilterEventChange = (eventId: string | undefined) => {
    setFilterEventId(eventId);
    setFilterTicketType(undefined);
  };

  const handleEventChange = (eventId: string) => {
    console.log('Event selected:', eventId);
    setSelectedEventId(eventId);
    form.setFieldValue('ticketTypeId', undefined);
    fetchEventTicketTypes(eventId);
    if (!editingTicket) clearCompanyPick(form);
  };

  const handleCreate = () => {
    setEditingTicket(null);
    setSelectedEventId(null);
    setTicketTypes([]);
    form.resetFields();
    form.setFieldsValue({
      status: 'PENDING',
      // #167 — alapértékek a kiadó űrlapon.
      //
      // `PARTICIPANT`, nem `GUEST`: ez az űrlap MINDEN kézi jegykiadás útja,
      // nem csak a vendégeké, és a mai viselkedés is résztvevőt hoz létre (a
      // Prisma `@default(PARTICIPANT)`-ja). Vendéget kiadni egy tudatos
      // választás legyen a legördülőben, ne az alapértelmezés műve.
      attendeeType: 'PARTICIPANT',
      // A levélküldés VISZONT alapból be van kapcsolva: aki kézzel visz fel
      // valakit, az jegyet akar adni neki, és a kód nélkül érkező résztvevő az
      // ajtóban derül ki. A CSV-import ezzel szemben alapból KI-kapcsolt, mert
      // ott egy kattintás több száz levelet jelentene.
      sendTicketEmail: true,
    });
    setModalVisible(true);
  };

  const handleEdit = (record: Ticket) => {
    setEditingTicket(record);
    setSelectedEventId(record.eventId);
    if (record.eventId) {
      fetchEventTicketTypes(record.eventId);
    }
    // Set company and fetch company users if ticket has company
    if (record.companyId) {
      setSelectedCompanyId(record.companyId);
      fetchCompanyUsers(record.companyId);
    } else {
      setSelectedCompanyId(null);
      fetchUsers();
    }
    form.setFieldsValue({
      ...record,
      firstName: record.User?.firstName || record.firstName || '',
      lastName: record.User?.lastName || record.lastName || '',
      email: record.User?.email || record.email || '',
      phone: record.phone || '',
      jobTitle: (record as any).jobTitle || '',
      // #167 — a `...record` már hozná, de a `null` az antd Inputban
      // vezérelt/vezéreletlen váltást okoz és React-figyelmeztetést ír. Az
      // üres sztringre normalizálás ugyanaz a minta, mint a fenti mezőknél.
      organizationName: record.organizationName || '',
      // A kategória a fenti `...record`-ból jön; ha az valaha szűkülne, a mező
      // üresen maradna és a mentés visszasorolná a jegyet — ezt a
      // `Tickets.reclassify.test.tsx` két próbája fogja meg.
      purchasedAt: record.purchasedAt ? dayjs(record.purchasedAt) : undefined,
      companyId: record.companyId,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/tickets/${id}`);
      message.success(t('tickets.deleted'));
      fetchTickets(1, 50);
      fetchAllTicketsStats(); // Refresh stats after deletion
    } catch (error) {
      message.error(t('tickets.deleteFailed'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingTicket) {
        // For editing, send all updatable fields
        const updateData = {
          status: values.status,
          companyId: values.companyId,
          firstName: values.firstName,
          lastName: values.lastName,
          // #167 — üres mezőből `undefined`, mint a létrehozáskor: a kulcs
          // kimarad, a szerver a jegy címéhez nem nyúl.
          email: values.email || undefined,
          phone: values.phone,
          jobTitle: values.jobTitle,
          // #167 — a névtáblára kiírt szervezet. Kinyomtatott kitűzőn az
          // elgépelést javítani kell tudni, ezért a szerkesztő is küldi.
          organizationName: values.organizationName,
          // #165 — átsorolás. Szponzori jegyen a mező zárolt, tehát a
          // VÁLTOZATLAN érték megy ki; a szerver azt átengedi, a változtatást
          // viszont 400-zal utasítja el. A panel kizárása kényelem, a valódi
          // védelem a szerveré.
          attendeeType: values.attendeeType,
        };
        await api.patch(`/tickets/${editingTicket.id}`, updateData);
        message.success(t('tickets.updated'));
      } else {
        // #167 — a kiadott jegy MOSTANTÓL az admin úton születik.
        //
        // A régi `POST /tickets` `PENDING` állapotban hozta létre, vagyis a
        // jegy fizetésre várt. Vendégnél ez értelmezhetetlen: nem fizet, de
        // érvényes belépőt kap, tehát `PAID`-nek kell lennie, különben az
        // ajtóban akad el. A `POST /tickets/admin/create` pontosan ezt adja,
        // ugyanazzal a bérlőhatár-ellenőrzéssel, és ez az út tudja a
        // kategóriát, a titulust és a szervezetnevet is fogadni.
        //
        // A `POST /tickets` végpont megmarad és változatlan — csak nincs
        // többé képernyős hívója.
        const ticketData = {
          ticketTypeId: values.ticketTypeId,
          eventId: values.eventId,
          companyId: values.companyPick?.companyId ?? null,
          // #167 — e-mail cím nélkül is kiadható a jegy (helyszíni felvitel).
          // `undefined`-ot küldünk, nem `null`-t: a DTO `@IsOptional()`-je a
          // hiányzó mezőt engedi át, egy explicit `null` viszont a
          // `@IsEmail()`-be futna, és a kérés EGÉSZE 400-at kapna.
          email: values.email || undefined,
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone || null,
          attendeeType: values.attendeeType,
          jobTitle: values.jobTitle || undefined,
          organizationName: values.organizationName || undefined,
        };

        const created = await api.post('/tickets/admin/create', ticketData);

        // #167 — a belépő QR kiküldése. Alapból bepipálva, de e-mail cím
        // nélkül nincs hova küldeni, ezért a cím megléte is feltétel.
        //
        // KÜLÖN hívás, nem a létrehozás mellékhatása: a jegy már létrejött, és
        // ha a levélküldés elhasal (rossz SMTP, lejárt kvóta), a jegyet NEM
        // szabad elveszíteni. Ezért a hiba külön üzenetet kap, a létrehozás
        // sikerét pedig nem vonja vissza.
        if (values.sendTicketEmail && values.email) {
          try {
            await api.post(`/tickets/${created.data.id}/send-email`);
            message.success(t('tickets.createdGuestMailed'));
          } catch {
            message.warning(t('tickets.createdGuestMailFailed'));
          }
        } else {
          message.success(t('tickets.createdGuest'));
        }
      }

      setModalVisible(false);
      fetchTickets(1, 50);
      fetchAllTicketsStats(); // Refresh stats after create/edit
      fetchUsers(); // Refresh users list
    } catch (error: any) {
      console.error('Ticket creation error:', error.response?.data);
      const errorMessage = error.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => message.error(msg));
      } else {
        message.error(errorMessage || t('tickets.operationFailed'));
      }
    }
  };

  const handleCheckIn = async (ticketNumber: string) => {
    try {
      await api.post(`/tickets/validate/${ticketNumber}`);
      message.success(t('tickets.validated'));
      fetchTickets(1, 50);
      fetchAllTicketsStats(); // Refresh stats after check-in
    } catch (error: any) {
      message.error(error.response?.data?.message || t('tickets.validateFailed'));
    }
  };

  const handleShowEmailPreview = (ticket: Ticket) => {
    // Get recipient email
    const email = ticket.User?.email || ticket.email;
    if (!email) {
      message.error(t('tickets.noEmail'));
      return;
    }

    // Get attendee name
    const attendeeName = ticket.User
      ? `${ticket.User.firstName || ''} ${ticket.User.lastName || ''}`.trim() || ticket.User.email
      : `${ticket.firstName || ''} ${ticket.lastName || ''}`.trim() || ticket.email;

    // Format event date
    const eventDate = ticket.Event?.startDate
      ? new Date(ticket.Event.startDate).toLocaleDateString(textLocaleTag(i18n.language), {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : t('tickets.notProvided');

    // Prepare email preview data
    const previewData = {
      to: email,
      subject: t('tickets.emailSubject', {
        event: ticket.Event?.title || t('tickets.eventFallback'),
      }),
      ticketNumber: ticket.ticketNumber,
      eventTitle: ticket.Event?.title || t('tickets.eventFallback'),
      eventDate,
      eventLocation: ticket.Event?.location,
      ticketType: ticket.TicketType?.name || 'Standard',
      price: ticket.TicketType?.price || 0,
      attendeeName,
    };

    setEmailPreviewData(previewData);
    setEmailPreviewTicket(ticket);
    setEmailPreviewModalVisible(true);
  };

  const handleSendTicketEmail = async () => {
    if (!emailPreviewTicket) return;

    try {
      await api.post(`/tickets/${emailPreviewTicket.id}/send-email`);
      message.success(t('tickets.emailSent'));
      setEmailPreviewModalVisible(false);
    } catch (error) {
      message.error(t('tickets.emailFailed'));
    }
  };

  const handleShowMagicLink = async (ticket: Ticket) => {
    const email = ticket.User?.email || ticket.email;
    if (!email) {
      message.error(t('tickets.noEmail'));
      return;
    }

    try {
      // Hitelesített admin végpont: tenant-scope-olt, és visszaadja a linket
      // (a publikus /public/tickets/magic-link biztonsági okból már nem ad tokent).
      const response = await api.post(`/tickets/${ticket.id}/magic-link`);

      if (response.data.magicLink) {
        setMagicLink(response.data.magicLink);
      } else {
        message.success(t('tickets.magicLinkSentEmail'));
        const eventSlug = ticket.Event?.slug || 'event';
        const placeholderLink = `${FRONTEND_URL}/event/${eventSlug}/magic-link/[TOKEN_SENT_VIA_EMAIL]`;
        setMagicLink(placeholderLink);
      }

      setSelectedMagicLinkTicket(ticket);
      setMagicLinkModalVisible(true);
    } catch (error: any) {
      message.error(t('tickets.magicLinkFailed'));
      console.error('Magic link generation error:', error);
    }
  };

  const handleCopyMagicLink = () => {
    navigator.clipboard.writeText(magicLink);
    message.success(t('tickets.linkCopied'));
  };

  const handleSendMagicLinkEmail = async () => {
    if (!selectedMagicLinkTicket) return;

    try {
      // A hitelesített admin végpont a link generálásakor egyben e-mailt is küld
      // a jegy címére, így itt egyszerűen újra meghívjuk.
      await api.post(`/tickets/${selectedMagicLinkTicket.id}/magic-link`);

      message.success(t('tickets.magicLinkSentToAddress'));
      setMagicLinkModalVisible(false);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || t('tickets.magicLinkSendFailed');
      message.error(errorMessage);
    }
  };

  const handlePrintExport = () => {
    if (!filterEventId) {
      message.error(t('tickets.exportNeedsEvent'));
      return;
    }
    setPrintExportModalVisible(true);
  };

  const handlePrintExportConfirm = async () => {
    try {
      setPrintExportModalVisible(false);
      message.loading(t('tickets.exportGenerating'), 0);

      const response = await api.get('/tickets/export/print', {
        params: { eventId: filterEventId },
        responseType: 'blob',
      });

      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'print_export.zip';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.destroy();
      message.success(t('tickets.exportDownloaded'));
    } catch (error: any) {
      message.destroy();
      console.error('Print export error:', error);
      const errorMessage = error.response?.data?.message || t('tickets.exportFailed');
      message.error(errorMessage);
    }
  };

  const showTicketDetails = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setDetailModalVisible(true);
  };

  const STATUS_META: Record<string, { label: string; color: string; bg: string; border: string }> =
    {
      PENDING: {
        label: t('tickets.statusPending'),
        color: '#ad6800',
        bg: '#fff7e6',
        border: '#ffd591',
      },
      PAID: { label: t('tickets.statusPaid'), color: '#237804', bg: '#f6ffed', border: '#b7eb8f' },
      USED: { label: t('tickets.statusUsed'), color: '#0958d9', bg: '#e6f4ff', border: '#91caff' },
      CANCELLED: {
        label: t('tickets.statusCancelled'),
        color: '#a8071a',
        bg: '#fff1f0',
        border: '#ffa39e',
      },
      // A jelentkezés-lánc három állapota, amit a backend a jegyre IS ír
      // (#139): eddig nyers enum-névvel, szürkén jelentek meg. A feliratok a
      // Regisztrációk oldaléval azonosak (`registrationStatus.ts`), hogy a két
      // képernyő ne mondjon két szót ugyanarra. A `REGISTERED`, `APPROVED` és
      // `ATTENDED` a Prisma enumban létezik, de semmi nem írja — nem kap sort.
      TICKET_REQUESTED: {
        label: t('tickets.statusTicketRequested'),
        color: '#531dab',
        bg: '#f9f0ff',
        border: '#d3adf7',
      },
      PAYMENT_INSTRUCTIONS_SENT: {
        label: t('tickets.statusPaymentInstructionsSent'),
        color: '#ad6800',
        bg: '#fff7e6',
        border: '#ffd591',
      },
      // A korábbi `EXPIRED` bejegyzés helyén: az nem `TicketStatus`-érték, semmi
      // nem írta; a valódi lejárt állapot a `PAYMENT_EXPIRED`.
      PAYMENT_EXPIRED: {
        label: t('tickets.statusPaymentExpired'),
        color: '#871400',
        bg: '#fff2e8',
        border: '#ffbb96',
      },
    };

  const StatusPill = ({ status }: { status: string }) => {
    const meta = STATUS_META[status] ?? {
      label: status,
      color: '#595959',
      bg: '#fafafa',
      border: '#d9d9d9',
    };
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '2px 10px',
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 500,
          color: meta.color,
          background: meta.bg,
          border: `1px solid ${meta.border}`,
          lineHeight: 1.6,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: meta.color }} />
        {meta.label}
      </span>
    );
  };

  const columns: ColumnsType<Ticket> = [
    {
      title: t('tickets.columnTicketNumber'),
      dataIndex: 'ticketNumber',
      key: 'ticketNumber',
      width: 230,
      render: (ticketNumber) => (
        <Text
          copyable={{ tooltips: [t('tickets.copyTooltip'), t('tickets.copiedTooltip')] }}
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12,
            letterSpacing: 0.2,
            whiteSpace: 'nowrap',
          }}
        >
          {ticketNumber}
        </Text>
      ),
    },
    {
      title: t('common.event'),
      key: 'event',
      width: 220,
      render: (_, record) =>
        record.Event ? (
          <div style={{ minWidth: 0 }}>
            <Text
              strong
              style={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 200,
              }}
              title={record.Event.title}
            >
              {record.Event.title}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(record.Event.startDate).format('YYYY.MM.DD HH:mm')}
            </Text>
          </div>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('tickets.columnCompany'),
      key: 'company',
      width: 200,
      responsive: ['lg'],
      render: (_, record) => {
        // #116 — a cégnév KÉT forrásból jöhet, és a sorrend kötött. Ha van
        // `Company` sor, azt mutatjuk, betű szerint úgy, ahogy eddig: az
        // adószámot gyűjtő események (Connect+Grow) oszlopa így nem mozdul.
        // Csak a `Company` sor HIÁNYÁBAN nézzük a jelentkezésen tárolt nevet.
        //
        // Ez azért nem kozmetika: az „Egyéni" felirat nem elhallgatta a céget,
        // hanem az ellenkezőjét állította. Egy olyan eseményen, amelynek a
        // saját űrlapja szerint kizárólag cégek jelentkezhetnek, minden jegy
        // magánszemélyként jelent meg.
        // #167 — a kézzel beírt szervezetnév nyer, ha ki van töltve. Ez a
        // negyedik felület ugyanazon a láncon; alatta a sorrend bájtra a régi.
        // Vendégnél (nincs `Company` sora és nincs jelentkezése) ez az egyetlen
        // forrás, enélkül a sor „Egyéni"-t írna egy meghívott politikusra.
        const companyName =
          record.organizationName?.trim() ||
          (record.Company ? record.Company.name : record.Registration?.companyName || '');

        // A címke MEGJELENÉSE is a `Company` sor létén múlik, nem a név
        // ürességén. Egy üres nevű `Company` sor ma üres címkét ad — ha itt a
        // névre szűrnénk, az „Egyéni"-re váltana, vagyis a javítás pont abba a
        // hibába esne, amit orvosolni akar: cégeset állítana magánszemélynek.
        // #167 — a beírt szervezetnév önmagában is megjeleníti a címkét, akkor
        // is, ha se `Company` sor, se jelentkezés nincs mögötte.
        return record.Company || companyName ? (
          <Tag
            bordered={false}
            icon={<TagsOutlined />}
            color="purple"
            style={{
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              padding: '2px 10px',
              fontSize: 12,
            }}
            title={companyName}
          >
            {companyName}
          </Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('tickets.individual')}
          </Text>
        );
      },
    },
    {
      title: t('tickets.columnOwner'),
      key: 'user',
      width: 240,
      render: (_, record) => <TicketOwnerCell ticket={record} />,
    },
    {
      title: t('common.type'),
      key: 'ticketType',
      width: 220,
      responsive: ['lg'],
      render: (_, record) =>
        record.TicketType ? (
          <Tag
            bordered={false}
            color="geekblue"
            style={{
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              padding: '2px 10px',
              fontSize: 12,
            }}
            title={record.TicketType.name}
          >
            {record.TicketType.name}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('tickets.columnStatus'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: [
        { text: t('tickets.statusPending'), value: 'PENDING' },
        { text: t('tickets.statusPaid'), value: 'PAID' },
        // Marad (#20): új sor már nem kap `USED`-et, de a régiek hordozzák,
        // és szűrhetőnek kell maradniuk. A jelenlétet a Validation oszlop
        // mutatja, nem ez.
        { text: t('tickets.statusUsed'), value: 'USED' },
        { text: t('tickets.statusCancelled'), value: 'CANCELLED' },
        { text: t('tickets.statusTicketRequested'), value: 'TICKET_REQUESTED' },
        { text: t('tickets.statusPaymentInstructionsSent'), value: 'PAYMENT_INSTRUCTIONS_SENT' },
        { text: t('tickets.statusPaymentExpired'), value: 'PAYMENT_EXPIRED' },
      ],
      // #166 — a szűrés a SZERVEREN történik, ezért nincs `onFilter`. A
      // `filteredValue` tartja szinkronban a fejléc jelölését az állapottal;
      // enélkül a „Szűrők törlése" gomb a kérést ürítené, a pipa viszont
      // ottmaradna a fejlécben.
      filterMultiple: false,
      filteredValue: filterStatus ? [filterStatus] : null,
      render: (status) => <StatusPill status={status} />,
    },
    {
      title: t('tickets.columnValidate'),
      key: 'validate',
      width: 110,
      responsive: ['md'],
      // #20: a `checkedIn`-re ágazik, nem a `status`-ra. Az ajtón bejutottnál
      // a `status` `PAID` maradt, ezért a gomb aktív maradt, és a személyzet
      // másodszor is „érvényesíthette" a már bent lévőt — figyelmeztetés nélkül.
      render: (_, record) =>
        record.checkedIn ? (
          <Text type="success" style={{ fontSize: 12 }}>
            <CheckCircleOutlined /> {t('tickets.checkedInLabel')}
          </Text>
        ) : (
          <Button
            size="small"
            type="primary"
            ghost
            onClick={() => handleCheckIn(record.ticketNumber)}
            disabled={record.status !== 'PAID'}
          >
            {t('tickets.validateButton')}
          </Button>
        ),
    },
    {
      title: t('common.actions'),
      key: 'action',
      width: 160,
      align: 'right' as const,
      render: (_, record) => (
        <Space size={0}>
          <Tooltip title={t('tickets.tooltipQr')}>
            <Button
              type="text"
              size="small"
              icon={<QrcodeOutlined />}
              onClick={() => showTicketDetails(record)}
            />
          </Tooltip>
          <Tooltip title={t('tickets.tooltipSendEmail')}>
            <Button
              type="text"
              size="small"
              icon={<SendOutlined />}
              onClick={() => handleShowEmailPreview(record)}
            />
          </Tooltip>
          <Tooltip title={t('tickets.tooltipMagicLink')}>
            <Button
              type="text"
              size="small"
              icon={<LinkOutlined />}
              style={{ color: '#722ed1' }}
              onClick={() => handleShowMagicLink(record)}
            />
          </Tooltip>
          <Tooltip title={t('tickets.tooltipEdit')}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('common.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Tooltip title={t('tickets.tooltipDelete')}>
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // No frontend filtering needed - backend handles search and filters
  const filteredTickets = tickets;

  // Stats for cards (all tickets from database)
  const stats = allTicketsStats || {
    total: 0,
    paid: 0,
    used: 0,
    pending: 0,
    revenueByCurrency: {} as Record<string, number>,
  };

  /**
   * Egy jegy pénzneme az eseménye alapján — vagy a megjelenítési tartalék.
   *
   * A jegyár négy helyen `€`-val volt bedrótozva (a jegytípus-választó, a
   * szerkesztő űrlap, a részletek és a levél-előnézet). Amióta a fenti
   * bevétel-csempe az esemény VALÓDI pénznemét írja ki, ez a négy hely
   * ellentmondott neki: ugyanaz a 6000 dinár fent „RSD", lent „€6000.00".
   *
   * Ismeretlen pénznemnél a mai `EUR` marad — itt egyetlen jegy áráról van
   * szó, ahol a jelöletlen szám többet zavarna, mint amennyit segít; a
   * címkézetlen összeg a csempén jelenik meg, ahol a szervező összegez.
   */
  const currencyOfEvent = (eventId?: string | null) =>
    (eventId && currencyByEventId.get(eventId)) || FALLBACK_CURRENCY;
  const priceText = (amount: number, eventId?: string | null) =>
    formatMoney(amount, currencyOfEvent(eventId), textLocaleTag(i18n.language));

  // Badge count shows filtered results count, or total if no filters applied
  const hasActiveFilters =
    searchText ||
    filterEventId ||
    filterCompanyId ||
    filterAttendeeType ||
    filterTicketType ||
    filterAssignmentStatus ||
    filterStatus;
  const badgeCount = hasActiveFilters ? filteredTicketsCount : stats.total;

  return (
    <div>
      <Row
        justify="space-between"
        align="middle"
        gutter={[12, 12]}
        wrap
        style={{ marginBottom: isMobile ? 16 : 24 }}
      >
        <Col xs={24} md="auto">
          <Title level={2} style={{ margin: 0 }}>
            {t('tickets.pageTitle')}{' '}
            <Badge
              count={badgeCount}
              overflowCount={999999}
              style={{
                backgroundColor: '#52c41a',
                fontSize: '14px',
                height: '24px',
                minWidth: '24px',
                lineHeight: '24px',
                borderRadius: '12px',
              }}
            />
          </Title>
        </Col>
        <Col xs={24} md="auto">
          <Space
            wrap
            size={[8, 8]}
            style={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}
          >
            <Button icon={<UploadOutlined />} onClick={() => setImportModalVisible(true)}>
              {isMobile ? t('tickets.importShort') : t('tickets.importCsv')}
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handlePrintExport}
              disabled={!filterEventId}
              title={
                !filterEventId ? t('tickets.exportTitleDisabled') : t('tickets.exportTitleEnabled')
              }
            >
              {isMobile ? t('tickets.exportShort') : t('tickets.exportPrint')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('tickets.newTicket')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Szűrők */}
      <Card style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input.Search
              placeholder={t('tickets.searchPlaceholder')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              style={{ width: '100%' }}
              placeholder={t('tickets.filterEventPlaceholder')}
              allowClear
              value={filterEventId}
              onChange={handleFilterEventChange}
              showSearch
              optionFilterProp="children"
            >
              {events.map((event) => (
                <Option key={event.id} value={event.id}>
                  {eventOptionLabel(event)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              style={{ width: '100%' }}
              placeholder={t('tickets.filterCategoryPlaceholder')}
              allowClear
              value={filterAttendeeType}
              onChange={setFilterAttendeeType}
            >
              {ATTENDEE_TYPES.map((value) => {
                const key = attendeeTypeLabelKey(value);
                return (
                  <Option key={value} value={value}>
                    {key ? t(key) : value}
                  </Option>
                );
              })}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              style={{ width: '100%' }}
              placeholder={t('tickets.filterCompanyPlaceholder')}
              allowClear
              value={filterCompanyId}
              onChange={setFilterCompanyId}
              showSearch
              optionFilterProp="children"
            >
              <Option value="individual">{t('tickets.filterOnlyIndividual')}</Option>
              <Option value="corporate">{t('tickets.filterOnlyCorporate')}</Option>
              {companies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={8} md={8} lg={3}>
            {/*
              #166 — VALÓDI jegytípusok. Eddig két opciója volt, „Céges" és
              „Egyéni" — vagyis a cég-szűrő első két elemének ismétlése, nem
              jegytípus. A szervező a Típus oszlopban „Teljes tagjegy"-et
              olvasott, a fölötte lévő szűrőben viszont ilyet nem talált.
            */}
            <Select
              style={{ width: '100%' }}
              placeholder={t('tickets.filterTypePlaceholder')}
              allowClear
              disabled={!filterEventId}
              value={filterTicketType}
              onChange={setFilterTicketType}
            >
              {filterTicketTypes.map((type) => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={8} md={8} lg={3}>
            <Select
              style={{ width: '100%' }}
              placeholder={t('tickets.filterAssignmentPlaceholder')}
              allowClear
              value={filterAssignmentStatus}
              onChange={setFilterAssignmentStatus}
            >
              <Option value="assigned">{t('tickets.assigned')}</Option>
              <Option value="unassigned">{t('tickets.tagUnassigned')}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={8} lg={4}>
            <Button
              block={isMobile}
              onClick={() => {
                setFilterEventId(undefined);
                setFilterAttendeeType(undefined);
                setFilterCompanyId(undefined);
                setFilterTicketType(undefined);
                setFilterAssignmentStatus(undefined);
                // #166 — a fejlécszűrő is a szűrők közé tartozik: enélkül a
                // gomb megnyomása után a lista továbbra is szűrve maradna, és
                // semmi nem mutatná, mitől.
                setFilterStatus(undefined);
                setSearchText('');
              }}
            >
              {t('tickets.clearFilters')}
            </Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={[12, 12]} style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Col xs={12} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('tickets.statTotal')}
              value={stats.total}
              prefix={<TagsOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('tickets.statusPaid')}
              value={stats.paid}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card>
            {/* A felirat a #20-ban „Használt"-ról „Beléptetve"-re váltott,
                mert a szám mostantól a belépetteket számolja, nem a `USED`
                állapotúakat. Meglévő kulcs, új fordítás nem kellett hozzá. */}
            <Statistic
              title={t('tickets.checkedInLabel')}
              value={stats.used}
              valueStyle={{ color: '#1890ff' }}
              suffix={`/ ${stats.total}`}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card>
            {(() => {
              const paid = (stats.paidRevenueByCurrency ?? {}) as Record<string, number>;
              const locale = textLocaleTag(i18n.language);
              const fmt = (v: number, ccy: string | null) => formatMoney(v, ccy, locale);

              /**
               * A vödrök `[pénznem, összeg]` párokként — az ISMERETLEN pénznem
               * `null` kulccsal, a lista VÉGÉN.
               *
               * Nem szabad az euróhoz adni: egy elhasalt config-lekérés azt
               * jelenti, hogy nem tudjuk a pénznemet, nem azt, hogy euró. A
               * `null` kulcsú sor saját, megjelölt összeget ír ki, pénznem-jel
               * nélkül — így a szervező látja, hogy van egy összeg, amit a
               * panel nem tud címkézni.
               */
              const buckets = Object.entries(stats.revenueByCurrency ?? {}) as [string, number][];
              const nonZero = buckets.filter(([, v]) => v > 0);
              /**
               * A nullás vödröket csak akkor dobjuk el, ha van mellettük
               * nem-nulla (#167).
               *
               * A szűrés eredetileg a zajt vágta le. A vendégjegy óta viszont
               * van olyan NORMÁLIS nézet, amiben minden vödör nulla — a Vendég
               * kategóriaszűrő alatt minden jegy vendégé, tehát nulla bevételű
               * —, és ott az üres lista a default EUR-ra esne vissza. A
               * szervező így egy dináros eseményen „0,00 EUR"-t olvasna: pont
               * az a hamis pénznem-címke, amit a #111 megszüntetett.
               */
              const known: [string | null, number][] = nonZero.length > 0 ? nonZero : buckets;
              const unknownTotal = (stats.unknownRevenue ?? 0) as number;
              const unknownPaid = (stats.unknownPaidRevenue ?? 0) as number;
              const entries: [string | null, number][] =
                unknownTotal > 0 ? [...known, [null, unknownTotal]] : known;
              const paidOf = (ccy: string | null) => (ccy === null ? unknownPaid : paid[ccy] || 0);
              /** A `null` vödör felirata a szám mellett — sosem egy kitalált kód. */
              const labelOf = (ccy: string | null) =>
                ccy === null ? t('tickets.unknownCurrency') : ccy;

              // Tömör sub-line: 11px, marginTop 0, hogy a Bevétel kártya
              // ne legyen magasabb a többi statisztikai kártyánál.
              const paidSubLine = (
                <div
                  style={{
                    marginTop: 0,
                    fontSize: 11,
                    lineHeight: 1.2,
                    color: '#52c41a',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {entries.length === 0
                    ? t('tickets.paidSubLine', { amount: fmt(0, FALLBACK_CURRENCY) })
                    : entries
                        .map(([ccy]) =>
                          t('tickets.paidSubLine', {
                            amount:
                              ccy === null
                                ? `${fmt(paidOf(ccy), null)} ${labelOf(ccy)}`
                                : fmt(paidOf(ccy), ccy),
                          })
                        )
                        .join(' · ')}
                </div>
              );

              // A főérték font-méretét 20-ra csökkentjük (default ~24), így
              // a sub-line elfér a többi kártya magasságán belül.
              const mainValueStyle = {
                color: '#722ed1',
                fontSize: 20,
                lineHeight: 1.2,
              } as const;

              /**
               * A főszámot a `formatter` írja ki, NEM az antd saját
               * `precision` + `suffix` párosa.
               *
               * Az antd a nyers számot a maga szabályai szerint csoportosítja
               * és a pénznem KÓDJÁT ragasztja a végére: `6,000.00RSD` állt a
               * saját „ebből fizetve: RSD 6,000.00" sora fölött. Két eltérő
               * alak ugyanazon a csempén, két centiméterre egymástól. A
               * többpénznemes ág (lentebb) mindig is a `fmt`-et használta —
               * ez az egy ág maradt ki, és éppen ez a gyakoribb: a legtöbb
               * szervezet egyetlen pénznemben árul.
               *
               * A `formatter` megkerüli a `precision`-t és a `groupSeparator`-t
               * is, tehát a kiírásnak EGY forrása marad az egész lapon.
               */
              const mainFigure = (ccy: string | null, val: number) =>
                ccy === null ? `${fmt(val, null)} ${labelOf(ccy)}` : fmt(val, ccy);

              if (entries.length === 0) {
                return (
                  <>
                    <Statistic
                      title={t('tickets.statRevenue')}
                      value={0}
                      formatter={() => fmt(0, FALLBACK_CURRENCY)}
                      valueStyle={mainValueStyle}
                    />
                    {paidSubLine}
                  </>
                );
              }
              if (entries.length === 1) {
                const [ccy, val] = entries[0];
                return (
                  <>
                    <Statistic
                      title={t('tickets.statRevenue')}
                      value={val}
                      formatter={() => mainFigure(ccy, val)}
                      valueStyle={mainValueStyle}
                    />
                    {paidSubLine}
                  </>
                );
              }
              // Több pénznem — egymás alatt listázzuk őket, mindegyik mellé
              // a saját ebből-fizetve sora. Tömör spacing.
              return (
                <div>
                  <div
                    style={{
                      color: 'rgba(0,0,0,0.45)',
                      fontSize: 14,
                      marginBottom: 4,
                    }}
                  >
                    {t('tickets.statRevenue')}
                  </div>
                  {entries.map(([ccy, val]) => (
                    <div key={ccy ?? '__unknown__'} style={{ marginBottom: 2 }}>
                      <div
                        style={{
                          color: '#722ed1',
                          fontSize: 18,
                          lineHeight: 1.2,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {mainFigure(ccy, val)}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          lineHeight: 1.2,
                          color: '#52c41a',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {t('tickets.paidSubLine', {
                          amount:
                            ccy === null
                              ? `${fmt(paidOf(ccy), null)} ${labelOf(ccy)}`
                              : fmt(paidOf(ccy), ccy),
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredTickets}
          rowKey="id"
          loading={loading}
          /**
           * A táblázat EGYETLEN `onChange`-e intézi a szűrést és a lapozást is,
           * az antd `extra.action` mezője alapján. A lapozás korábban a
           * `pagination.onChange`-ben ült, és ez egy néma pazarlást okozott:
           * szűrő alkalmazásakor az antd az első oldalra ugrik, ami MEGHÍVJA a
           * lapozó `onChange`-ét, az pedig elindított egy `fetchTickets`-et a
           * még RÉGI szűrőállapottal. Minden szűrőkattintás két lista-kérést
           * fizetett, és a szervező egy villanást látott: a lista egy
           * pillanatra visszaugrott a szűretlen halmazra.
           *
           * A szűrés ága szándékosan NEM kérdez le: a `filterStatus` állítása
           * elindítja a fenti hatást, ami a listát és a csempéket EGYÜTT tölti
           * újra (#122). Aki ide is tenne egy `fetchTickets`-et, az a
           * táblázatot a csempék nélkül frissítené.
           */
          onChange={(pagination, filters, _sorter, extra) => {
            if (extra.action === 'filter') {
              const picked = filters?.status?.[0];
              setFilterStatus(typeof picked === 'string' ? picked : undefined);
              return;
            }
            if (extra.action === 'paginate') {
              fetchTickets(pagination.current ?? 1, pagination.pageSize ?? 50);
            }
          }}
          scroll={{ x: 'max-content' }}
          size={isMobile ? 'small' : 'middle'}
          pagination={{
            showSizeChanger: !isMobile,
            simple: isMobile,
            // #164 — a lapozó a SZŰRT találatszámmal dolgozik.
            //
            // Eddig a `stats.total` táplálta, ami a szűretlen, szervezet-szintű
            // statisztika-kérésből jön (`fetchAllTicketsStats`, `limit: 10000`).
            // Aktív kereséssel a lapozó ezért olyan oldalakat kínált, amik
            // üresen jöttek vissza — a szervező ebből azt olvasta ki, hogy a
            // keresés romlott el, nem azt, hogy a lapozó téved.
            //
            // A `filteredTicketsCount` a listakérés `meta.total`-ja, tehát
            // mindig annyi, ahány sor a MOSTANI szűrőkre illik. Szűrő nélkül
            // megegyezik a `stats.total`-lal.
            showTotal: (total, range) =>
              t('tickets.paginationTotal', {
                from: range[0],
                to: range[1],
                count: total,
              }),
            defaultPageSize: 50,
            pageSizeOptions: ['20', '50', '100', '200'],
            // A lapozás kiszolgálása a táblázat `onChange`-ében van — lásd az
            // ottani indoklást. Itt egy `onChange` a szűrő-alkalmazáskori
            // oldalvisszaállásra is elsülne, és megint két kérés menne.
            total: filteredTicketsCount,
          }}
        />
      </Card>

      <Modal
        title={editingTicket ? t('tickets.modalEditTitle') : t('tickets.modalNewTitle')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="eventId"
                label={t('common.event')}
                rules={[{ required: !editingTicket, message: t('common.requiredField') }]}
              >
                <Select
                  placeholder={t('tickets.selectEventPlaceholder')}
                  onChange={handleEventChange}
                  disabled={!!editingTicket}
                >
                  {events.map((event) => (
                    <Option key={event.id} value={event.id}>
                      {eventOptionLabel(event)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="ticketTypeId"
                label={t('tickets.labelTicketType')}
                rules={[{ required: !editingTicket, message: t('common.requiredField') }]}
              >
                <Select
                  placeholder={
                    selectedEventId
                      ? t('tickets.selectTicketTypePlaceholder')
                      : t('tickets.selectEventFirst')
                  }
                  disabled={!selectedEventId || !!editingTicket}
                >
                  {ticketTypes.map((type) => (
                    <Option key={type.id} value={type.id}>
                      <Space>
                        <span>{type.name}</span>
                        <Text type="secondary">{priceText(type.price, selectedEventId)}</Text>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {editingTicket ? (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="companyId"
                  label={t('tickets.labelCompanyOptional')}
                  extra={t('tickets.companyExtra')}
                >
                  <Select
                    placeholder={t('tickets.selectCompanyPlaceholder')}
                    allowClear
                    onChange={(companyId) => {
                      setSelectedCompanyId(companyId);
                      if (companyId) {
                        fetchCompanyUsers(companyId);
                      } else {
                        fetchUsers();
                      }
                      form.setFieldValue('userId', undefined);
                    }}
                    showSearch
                    optionFilterProp="children"
                  >
                    {companies.map((company) => (
                      <Option key={company.id} value={company.id}>
                        {company.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Row gutter={16}>
              <Col span={24}>
                {/* Own lists: `companies` stays the list filter's, narrowed to active events. */}
                <Form.Item
                  name="companyPick"
                  label={t('tickets.labelCompanyOptional')}
                  extra={t('tickets.companyPickerExtra')}
                >
                  <NewTicketCompanyPicker event={events.find((e) => e.id === selectedEventId)} />
                </Form.Item>
              </Col>
            </Row>
          )}

          {!editingTicket && (
            <>
              {/* Ticket holder information - Only for new tickets */}
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="firstName"
                    label={t('tickets.labelFirstName')}
                    rules={[{ required: true, message: t('common.requiredField') }]}
                  >
                    <Input placeholder={t('tickets.labelFirstName')} prefix={<UserOutlined />} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="lastName"
                    label={t('tickets.labelLastName')}
                    rules={[{ required: true, message: t('common.requiredField') }]}
                  >
                    <Input placeholder={t('tickets.labelLastName')} prefix={<UserOutlined />} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  {/*
                    #167 — az e-mail cím itt MÁR NEM kötelező.

                    A helyszíni felvitelnek ez a lényege: az ajtónál megjelenő,
                    listán nem szereplő embernek azonnal jegyet kell kapnia, és
                    ott gyakran nincs e-mail címe — vagy nincs idő elkérni. Az
                    adatbázis oszlopa eddig is nullable volt, csak a DTO és ez
                    az űrlap követelte meg.

                    A formátum-ellenőrzés MEGMARAD: ha írnak be címet, annak
                    érvényesnek kell lennie. Üresen hagyni szabad, elgépelni nem.
                  */}
                  <Form.Item
                    name="email"
                    label={t('tickets.labelEmailOptional')}
                    extra={t('tickets.emailOptionalExtra')}
                    rules={[{ type: 'email', message: t('tickets.invalidEmail') }]}
                  >
                    <Input prefix={<MailOutlined />} placeholder={t('tickets.emailPlaceholder')} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="phone" label={t('tickets.labelPhone')}>
                    <Input prefix={<PhoneOutlined />} placeholder={t('tickets.phonePlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>

              {/*
                #167 — kategória, titulus és szervezet a KIADÁS pillanatában.

                Eddig mindhárom csak utólag, a jegy szerkesztésével volt
                beállítható — a vendégjegyet tehát kétszer kellett érinteni.
                A kategória ráadásul a névtáblát, a statisztikát és az ajtó
                képernyőjét is hajtja, vagyis rossz alapértékkel a „vendég"
                résztvevőként jelenne meg mindhárom helyen.
              */}
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="attendeeType" label={t('tickets.fieldCategory')}>
                    <Select>
                      {ATTENDEE_TYPES.map((value) => {
                        const key = attendeeTypeLabelKey(value);
                        return (
                          <Option key={value} value={value}>
                            {key ? t(key) : value}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="jobTitle" label={t('tickets.labelJobTitle')}>
                    <Input placeholder={t('tickets.jobTitlePlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24}>
                  {/*
                    #167 — a névtáblára kiírt szervezet, szabad szövegként.

                    A fenti „Cég" mező egy valódi, adószámos `Company` sort
                    választ ki. A vendéghez ilyen nem tartozik — nincs cége a
                    rendszerben, és nem is lesz —, ezért nélküle a névtáblájára
                    csak a neve kerülhetne. Ez a mező az egyetlen forrás nála.

                    Kitöltve minden felületen ez nyer (ajtó, Jegyek oldal,
                    nyomtatási export, névtábla); üresen hagyva minden a mai
                    forrásaiból dolgozik tovább.
                  */}
                  <Form.Item
                    name="organizationName"
                    label={t('tickets.labelOrganizationName')}
                    extra={t('tickets.organizationNameExtra')}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              {/*
                #167 — a belépő QR kiküldése, alapból bepipálva.

                A jegy létrehozása önmagában SOSEM küldött levelet; ez külön
                végpont volt, a listából, jegyenként. Aki eddig kézzel vitt fel
                valakit, annak külön eszébe kellett jutnia elküldeni — enélkül
                a résztvevő kód nélkül állt az ajtóban.

                E-mail cím nélkül nincs hova küldeni; ilyenkor a jelölő némán
                hatástalan, és a jegy a pultnál nyomtatható ki.
              */}
              <Form.Item
                name="sendTicketEmail"
                valuePropName="checked"
                extra={t('tickets.sendTicketEmailExtra')}
              >
                <Checkbox>{t('tickets.sendTicketEmailLabel')}</Checkbox>
              </Form.Item>
            </>
          )}

          {editingTicket && (
            <>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="ticketNumber" label={t('tickets.columnTicketNumber')}>
                    <Input prefix={<QrcodeOutlined />} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="status" label={t('tickets.columnStatus')}>
                    <Select>
                      <Option value="PENDING">{t('tickets.statusPending')}</Option>
                      <Option value="PAID">{t('tickets.statusPaid')}</Option>
                      {/* A `USED` kikerült (#20): a belépés a `checkedIn`
                          mezőé, és kézzel állítva a két nyom szétcsúszott.
                          Beléptetni az Érvényesítés gombbal, a Beléptetés
                          oldalon vagy az ajtó-alkalmazásból lehet. A meglévő
                          `USED` sorok megmaradnak és szűrhetők. */}
                      <Option value="CANCELLED">{t('tickets.statusCancelled')}</Option>
                      {/* A jelentkezés-lánc három állapota LETILTVA (#139): a
                          Select így feliratot mutat egy ilyen jegyen a nyers
                          enum-név helyett, de egy admin nem állíthatja a jegyet
                          olyan állapotba, amit csak a jelentkezés ír — a kettő
                          különben szétcsúszna. */}
                      <Option value="TICKET_REQUESTED" disabled>
                        {t('tickets.statusTicketRequested')}
                      </Option>
                      <Option value="PAYMENT_INSTRUCTIONS_SENT" disabled>
                        {t('tickets.statusPaymentInstructionsSent')}
                      </Option>
                      <Option value="PAYMENT_EXPIRED" disabled>
                        {t('tickets.statusPaymentExpired')}
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="attendeeType"
                    label={t('tickets.fieldCategory')}
                    extra={editingTicket.sponsorId ? t('tickets.categoryLockedSponsor') : undefined}
                  >
                    <Select disabled={!!editingTicket.sponsorId}>
                      {ATTENDEE_TYPES.map((value) => {
                        const key = attendeeTypeLabelKey(value);
                        return (
                          <Option key={value} value={value}>
                            {key ? t(key) : value}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* Tulajdonos adatok szerkesztése */}
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="firstName"
                    label={t('tickets.labelFirstName')}
                    rules={[{ required: true, message: t('common.requiredField') }]}
                  >
                    <Input placeholder={t('tickets.labelFirstName')} prefix={<UserOutlined />} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="lastName"
                    label={t('tickets.labelLastName')}
                    rules={[{ required: true, message: t('common.requiredField') }]}
                  >
                    <Input placeholder={t('tickets.labelLastName')} prefix={<UserOutlined />} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  {/*
                    #167 — ugyanaz a szabály, mint a létrehozáskor: ha van
                    cím, legyen érvényes; e-mail nélküli jegyen maradhat üresen.

                    A kötelezőség a jegy MEGNYITÁSKORI állapotán múlik. Egy
                    helyszíni vendégjegy cím nélkül született, és a feltétlen
                    `required` miatt a szervezete, a beosztása és a kategóriája
                    sem volt javítható. Egy címmel rendelkező jegyen viszont a
                    mező kiürítése nem törlés — a szerver az üres mezőt „nincs
                    változás"-nak veszi —, ezért ott a mező kötelező marad, és
                    a szervező nem hiheti, hogy a címet eltávolította.
                  */}
                  <Form.Item
                    name="email"
                    label={t(
                      editingTicketHasEmail ? 'tickets.labelEmail' : 'tickets.labelEmailOptional'
                    )}
                    rules={[
                      { required: editingTicketHasEmail, message: t('common.requiredField') },
                      { type: 'email', message: t('tickets.invalidEmail') },
                    ]}
                  >
                    <Input placeholder={t('tickets.emailPlaceholder')} prefix={<MailOutlined />} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="phone" label={t('tickets.labelPhone')}>
                    <Input placeholder={t('tickets.phonePlaceholder')} prefix={<PhoneOutlined />} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="jobTitle" label={t('tickets.labelJobTitle')}>
                <Input placeholder={t('tickets.jobTitlePlaceholder')} />
              </Form.Item>

              {/*
                #167 — a névtáblára kiírt szervezet a szerkesztőben is.
                A kitűzőt kinyomtatják; egy elgépelt szervezetnév csak itt
                javítható. Üresre törölve a lánc visszaesik a mai forrásokra
                (`Company` sor, majd a jelentkezés cégneve).
              */}
              <Form.Item
                name="organizationName"
                label={t('tickets.labelOrganizationName')}
                extra={t('tickets.organizationNameExtra')}
              >
                <Input />
              </Form.Item>

              {/* Jegy típus információ */}
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label={t('tickets.labelTicketType')}>
                    <Input value={editingTicket.TicketType?.name || '-'} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label={t('tickets.labelPrice')}>
                    <Input
                      value={
                        editingTicket.TicketType?.price
                          ? priceText(editingTicket.TicketType.price, editingTicket.eventId)
                          : '-'
                      }
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Check-in információk */}
              {editingTicket.checkedIn && (
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item label={t('tickets.labelCheckinStatus')}>
                      <Input
                        value={t('tickets.checkedInValue')}
                        disabled
                        style={{ color: '#52c41a' }}
                        prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label={t('tickets.labelCheckinTime')}>
                      <Input
                        value={
                          editingTicket.checkedInAt
                            ? dayjs(editingTicket.checkedInAt).format('YYYY.MM.DD HH:mm')
                            : '-'
                        }
                        disabled
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingTicket ? t('tickets.save') : t('common.create')}
              </Button>
              <Button onClick={() => setModalVisible(false)}>{t('tickets.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('tickets.detailTitle')}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            {t('common.close')}
          </Button>,
        ]}
        width={600}
      >
        {selectedTicket && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <QRCode
                value={
                  selectedTicket.qrCode ||
                  `${selectedTicket.eventId}-${selectedTicket.ticketNumber}`
                }
                size={200}
              />
              <div style={{ marginTop: 16 }}>
                <Text strong style={{ fontSize: 18 }}>
                  {selectedTicket.ticketNumber}
                </Text>
              </div>
            </div>

            <Descriptions bordered column={1}>
              <Descriptions.Item label={t('common.event')}>
                {selectedTicket.Event?.title}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.time')}>
                {selectedTicket.Event &&
                  dayjs(selectedTicket.Event.startDate).format('YYYY.MM.DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label={t('tickets.descLocation')}>
                {selectedTicket.Event?.location || t('tickets.online')}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.name')}>
                {selectedTicket.User
                  ? `${selectedTicket.User.firstName || ''} ${selectedTicket.User.lastName || ''}`.trim() ||
                    selectedTicket.User.email
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.email')}>
                {selectedTicket.User?.email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.type')}>
                <Tag color="blue">{selectedTicket.TicketType?.name || '-'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('tickets.labelPrice')}>
                {priceText(selectedTicket.TicketType?.price || 0, selectedTicket.eventId)}
              </Descriptions.Item>
              <Descriptions.Item label={t('tickets.columnStatus')}>
                <StatusPill status={selectedTicket.status} />
              </Descriptions.Item>
              <Descriptions.Item label={t('tickets.descPurchasedAt')}>
                {dayjs(selectedTicket.purchasedAt).format('YYYY.MM.DD HH:mm')}
              </Descriptions.Item>
              {/* #20: a `checkedIn` dönt, nem a `status`. Ha van belépési
                  időbélyeg, azt is kiírjuk — az ajtón bejutottnál eddig sem a
                  blokk, sem az idő nem jelent meg. */}
              {selectedTicket.checkedIn && (
                <Descriptions.Item label={t('tickets.descUsageStatus')}>
                  <Badge
                    status="success"
                    text={
                      selectedTicket.checkedInAt
                        ? `${t('tickets.checkedInValue')} — ${dayjs(selectedTicket.checkedInAt).format('YYYY.MM.DD HH:mm')}`
                        : t('tickets.checkedInValue')
                    }
                  />
                </Descriptions.Item>
              )}
            </Descriptions>
          </Space>
        )}
      </Modal>

      {/* Magic Link Modal */}
      <Modal
        title={t('tickets.magicLinkTitle')}
        open={magicLinkModalVisible}
        onCancel={() => setMagicLinkModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedMagicLinkTicket && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text strong>{t('tickets.magicLinkOwner')}</Text>
              <Text>
                {selectedMagicLinkTicket.User?.firstName || selectedMagicLinkTicket.firstName}{' '}
                {selectedMagicLinkTicket.User?.lastName || selectedMagicLinkTicket.lastName}
              </Text>
            </div>

            <div>
              <Text strong>{t('tickets.magicLinkEmailLabel')}</Text>
              <Text>{selectedMagicLinkTicket.User?.email || selectedMagicLinkTicket.email}</Text>
            </div>

            <div>
              <Text strong>{t('tickets.magicLinkLabel')}</Text>
              <div
                style={{
                  marginTop: 16,
                  padding: 20,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 8,
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    fontSize: 16,
                    fontFamily: 'monospace',
                    color: '#1890ff',
                    wordBreak: 'break-all',
                    lineHeight: 1.6,
                    paddingRight: 40,
                  }}
                >
                  {magicLink}
                </div>
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={handleCopyMagicLink}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    padding: '4px 8px',
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    transition: 'all 0.3s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                    e.currentTarget.style.borderColor = '#1890ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#d9d9d9';
                  }}
                />
              </div>
            </div>

            <Row gutter={16} style={{ marginTop: 20 }}>
              <Col span={24}>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendMagicLinkEmail}
                  style={{ width: '100%' }}
                  size="large"
                >
                  {t('tickets.sendByEmail')}
                </Button>
              </Col>
            </Row>

            <div
              style={{
                padding: '12px',
                backgroundColor: '#f0f2f5',
                borderRadius: '4px',
                marginTop: '8px',
              }}
            >
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('tickets.magicLinkHint')}
              </Text>
            </div>
          </Space>
        )}
      </Modal>

      {/* Email Preview Modal */}
      <Modal
        title={t('tickets.emailPreviewTitle')}
        open={emailPreviewModalVisible}
        onCancel={() => setEmailPreviewModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setEmailPreviewModalVisible(false)}>
            {t('tickets.cancel')}
          </Button>,
          <Button key="send" type="primary" icon={<SendOutlined />} onClick={handleSendTicketEmail}>
            {t('tickets.send')}
          </Button>,
        ]}
        width={800}
      >
        {emailPreviewData && (
          <div>
            {/* Email Header Info */}
            <Card style={{ marginBottom: 16 }} size="small">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={8}>
                  <Text strong>{t('tickets.previewTo')}</Text>
                  <br />
                  <Text type="secondary">{emailPreviewData.to}</Text>
                </Col>
                <Col xs={24} sm={16}>
                  <Text strong>{t('tickets.previewSubject')}</Text>
                  <br />
                  <Text type="secondary">{emailPreviewData.subject}</Text>
                </Col>
              </Row>
            </Card>

            {/* Email Content Preview */}
            <Card title={t('tickets.previewContentCard')} size="small">
              <div
                style={{
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: '1.6',
                  color: '#333',
                  padding: '20px',
                  backgroundColor: '#f4f4f4',
                  borderRadius: '10px',
                }}
              >
                {/* Header */}
                <div
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    padding: '20px',
                    textAlign: 'center',
                    borderRadius: '10px 10px 0 0',
                    margin: '-20px -20px 20px -20px',
                  }}
                >
                  <h1 style={{ margin: 0 }}>{t('tickets.previewBrand')}</h1>
                  <p style={{ margin: '5px 0 0 0' }}>{t('tickets.previewHeaderSub')}</p>
                </div>

                <h2>{t('tickets.previewGreeting', { name: emailPreviewData.attendeeName })}</h2>

                <p>{t('tickets.previewIntro')}</p>

                {/* Ticket Info */}
                <div
                  style={{
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '10px',
                    margin: '20px 0',
                    borderLeft: '4px solid #3b82f6',
                  }}
                >
                  <h3 style={{ marginTop: 0, color: '#3b82f6' }}>{emailPreviewData.eventTitle}</h3>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      margin: '10px 0',
                      padding: '5px 0',
                      borderBottom: '1px solid #eee',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 'bold',
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px',
                          backgroundColor: '#fee2e2',
                          borderRadius: '50%',
                          marginRight: '8px',
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#ef4444">
                          <path d="M9 1v2h6V1h2v2h4a1 1 0 011 1v16a1 1 0 01-1 1H3a1 1 0 01-1-1V4a1 1 0 011-1h4V1h2zm9 10v2H6v-2h12z" />
                        </svg>
                      </span>
                      {t('tickets.previewDate')}
                    </span>
                    <span>{emailPreviewData.eventDate}</span>
                  </div>

                  {emailPreviewData.eventLocation && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        margin: '10px 0',
                        padding: '5px 0',
                        borderBottom: '1px solid #eee',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 'bold',
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#fef3c7',
                            borderRadius: '50%',
                            marginRight: '8px',
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                          </svg>
                        </span>
                        {t('tickets.previewLocation')}
                      </span>
                      <span>{emailPreviewData.eventLocation}</span>
                    </div>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      margin: '10px 0',
                      padding: '5px 0',
                      borderBottom: '1px solid #eee',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 'bold',
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px',
                          backgroundColor: '#e0e7ff',
                          borderRadius: '50%',
                          marginRight: '8px',
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#6366f1">
                          <path d="M22 10v12H2V10l10-8 10 8zM8 14v6h8v-6H8zm2 2h4v2h-4v-2z" />
                        </svg>
                      </span>
                      {t('tickets.previewTicketType')}
                    </span>
                    <span>{emailPreviewData.ticketType}</span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      margin: '10px 0',
                      padding: '5px 0',
                      borderBottom: '1px solid #eee',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 'bold',
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px',
                          backgroundColor: '#dcfce7',
                          borderRadius: '50%',
                          marginRight: '8px',
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#22c55e">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
                        </svg>
                      </span>
                      {t('tickets.previewPrice')}
                    </span>
                    <span>{priceText(emailPreviewData.price, emailPreviewTicket?.eventId)}</span>
                  </div>
                </div>

                {/* QR Section */}
                <div
                  style={{
                    textAlign: 'center',
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '10px',
                    margin: '20px 0',
                  }}
                >
                  <p>
                    <strong>{t('tickets.previewTicketNumber')}</strong>
                  </p>
                  <div
                    style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: '#3b82f6',
                      margin: '10px 0',
                      letterSpacing: '2px',
                    }}
                  >
                    {emailPreviewData.ticketNumber}
                  </div>
                  <p style={{ marginTop: '20px', color: '#666', fontSize: '14px' }}>
                    {t('tickets.previewShowAtEntrance')}
                  </p>
                </div>

                {/* Tip Box */}
                <div
                  style={{
                    backgroundColor: '#f0f9ff',
                    padding: '15px',
                    borderRadius: '10px',
                    margin: '20px 0',
                  }}
                >
                  <p style={{ margin: 0, color: '#1e40af', display: 'flex', alignItems: 'center' }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#fbbf24',
                        borderRadius: '50%',
                        marginRight: '8px',
                        flexShrink: 0,
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="#ffffff">
                        <path d="M12 2C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2.85 11.1l-.85.6V16h-4v-2.3l-.85-.6C7.8 12.16 7 10.63 7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1z" />
                      </svg>
                    </span>
                    <span>
                      <strong>{t('tickets.previewTipLabel')}</strong> {t('tickets.previewTipBody')}
                    </span>
                  </p>
                </div>

                {/* Footer */}
                <div
                  style={{
                    marginTop: '30px',
                    textAlign: 'center',
                    fontSize: '12px',
                    color: '#666',
                  }}
                >
                  <p>{t('tickets.previewContact')}</p>
                  <p>{t('tickets.previewCopyright')}</p>
                </div>
              </div>
            </Card>
          </div>
        )}
      </Modal>

      {/* Print Export Confirmation Modal */}
      <Modal
        title={
          <Space>
            <PrinterOutlined style={{ color: '#1890ff' }} />
            <span>{t('tickets.printExportTitle')}</span>
          </Space>
        }
        open={printExportModalVisible}
        onCancel={() => setPrintExportModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setPrintExportModalVisible(false)}>
            {t('tickets.cancel')}
          </Button>,
          <Button
            key="export"
            type="primary"
            icon={<FileZipOutlined />}
            onClick={handlePrintExportConfirm}
          >
            {t('tickets.exportStart')}
          </Button>,
        ]}
        width={600}
      >
        <div style={{ padding: '20px 0' }}>
          <div
            style={{
              backgroundColor: '#f0f9ff',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #d1ecf1',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <FileZipOutlined style={{ fontSize: '20px', color: '#0ea5e9', marginRight: '8px' }} />
              <Text strong style={{ fontSize: '16px' }}>
                {t('tickets.exportContents')}
              </Text>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>
                <Text>
                  📊 <strong>{t('tickets.exportCsvLabel')}</strong>
                  {t('tickets.exportCsvDesc')}
                </Text>
              </li>
              <li>
                <Text>
                  🔲 <strong>{t('tickets.exportQrLabel')}</strong>
                  {t('tickets.exportQrDesc')}
                </Text>
              </li>
              <li>
                <Text>
                  🗜️ <strong>{t('tickets.exportZipLabel')}</strong>
                  {t('tickets.exportZipDesc')}
                </Text>
              </li>
            </ul>
          </div>

          <div
            style={{
              backgroundColor: '#fff7e6',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #ffd591',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <PrinterOutlined style={{ fontSize: '18px', color: '#fa8c16', marginRight: '8px' }} />
              <Text strong>{t('tickets.exportSelectedEvent')}</Text>
            </div>
            <Text style={{ fontSize: '16px', color: '#fa8c16' }}>
              {events.find((e) => e.id === filterEventId)?.title || t('tickets.unknownEvent')}
            </Text>
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary">
                {t('tickets.exportTicketCount')}
                <strong>{filteredTicketsCount}</strong>
              </Text>
            </div>
          </div>

          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {t('tickets.exportDurationHint')}
            </Text>
          </div>
        </div>
      </Modal>

      <TicketImportModal
        visible={importModalVisible}
        onClose={() => setImportModalVisible(false)}
        // Az `onSuccess` FRISSÍT, de nem zár. Eddig bezárta a modált, és mivel
        // az import épp előtte lépett az „Eredmények" lépésre, a szervező soha
        // nem látta az eredményt — egy képkockára jelent meg, aztán eltűnt.
        // A #104 óta ez a panel mondja meg, hány jegylevél ment ki és hány
        // bukott; e nélkül a jelölőnégyzet megint csak egy ígéret lenne.
        // A bezárás a szervezőé: az eredmény-lépésen ott a Bezárás gomb.
        onSuccess={() => {
          fetchTickets(1, 50);
          fetchAllTicketsStats(); // Refresh stats after import
        }}
      />
    </div>
  );
};

export default Tickets;
